<template>
  <InterfaceCard
    icon="mdi-currency-usd"
    title="Подтверждение платежа"
  >
    <div class="confirmation-content">
      <v-skeleton-loader
        v-if="loading"
        type="card"
      />
      <Info
        v-else-if="error"
        :error="error"
      />
      <div v-else>
        <v-form
          v-model="formIsValid"
          ref="confirmationForm"
          @submit="submit"
        >
          <div
            v-if="!isNullFullPrepayment"
            class="mb-4"
          >
            <PaymentConfirmationOptionSelector
              :options="paymentInfos"
              :selected-id="invoice.confirmation.paymentInfoId"
              @change="paymentInfoChanged"
            />
          </div>

          <div
            v-if="!confirmationTypeIsForm && !isNullFullPrepayment"
            class="confirmation-image"
          >
            <input
              id="file-input"
              type="file"
              accept="image/*,.pdf"
              ref="fileInput"
              @change="fileLoad"
            />

            <div class="image-wrapper">
              <div
                v-if="applicationFileName"
                class="file-name text-caption mb-4"
              >
                <div class="mb-2">
                  <v-icon>mdi-paperclip</v-icon>
                  <b>Прикреплен файл:</b>
                </div>
                {{applicationFileName}}
              </div>
              <img
                v-else
                class="outline-no"
                :src="receipt"
                @click="fileSelect"
              />
              <div
                v-if="!confirmationImageIsValid"
                class="red--text text-center mb-5"
              >
                <span class="body-1">{{loadReceiptBtnTitle}}</span>
              </div>
              <v-btn
                class="file-select-btn"
                color="primary"
                type="button"
                small
                @click="fileSelect"
              >
                <v-icon left>mdi-upload</v-icon>
                {{labelUploadButton}}
              </v-btn>
            </div>
          </div>

          <div
            v-if="invoiceModel.description"
            v-text="invoiceModel.description"
            class="my-5"
          />

          <div v-if="!isNullFullPrepayment && toBalance && balanceCcy === ccy">
            <v-checkbox
              v-model="toBalance.active"
              :label="labelToBalanceActivator"
              :ripple="false"
              @change="changeToBalanceActive"
            />
          </div>

          <v-text-field
            v-if="toBalance && toBalance.active"
            v-model="toBalance.amountReal"
            disabled
            prepend-icon="mdi-cash"
            :label="labelAmount"
            :rules="formRules.amountToBalance"
          />

          <v-text-field
            v-else-if="!isNullFullPrepayment"
            v-model="invoice.confirmation.amount"
            prepend-icon="mdi-cash"
            type="number"
            :label="labelAmount"
            :rules="formRules.amount"
          />

          <v-text-field
            v-else
            v-model="invoice.confirmation.amount"
            prepend-icon="mdi-cash"
            type="number"
            disabled
            :label="labelAmount"
          />

          <v-alert
            v-if="invoiceContext.hasPrepayment && (invoice.prepayment.to >= invoice.amount) && (invoice.confirmation.amount >= invoice.amount)"
            type="success"
          >
            Введена полная сумма. Счет будет оплачен полностью.
          </v-alert>

          <v-alert
            v-if="isNullFullPrepayment"
            type="info"
          >
            Сумма счета была изменена, и внесенная ранее предоплата достаточна для полной оплаты.
          </v-alert>

          <div v-if="confirmationTypeIsForm">
            <v-row>
              <v-col>
                <v-menu
                  v-model="showDatePicker"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                  :close-on-content-click="false"
                  :nudge-right="40"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="ruConfirmationDateFormatted"
                      label="Дата платежа"
                      prepend-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      :rules="formRules.date"
                    />
                  </template>
                  <v-date-picker
                    locale="ru"
                    v-model="isoConfirmationDateFormatted"
                    :max="today"
                    @input="showDatePicker = false"
                  />
                </v-menu>
              </v-col>
              <v-col>
                <v-menu
                  ref="menu"
                  v-model="showTimePicker"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="290px"
                  :close-on-content-click="false"
                  :nudge-right="40"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="isoConfirmationTimeFormatted"
                      label="Время"
                      prepend-icon="mdi-clock-time-four-outline"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      :rules="formRules.dateTime"
                    />
                  </template>
                  <v-time-picker
                    v-if="showTimePicker"
                    format="24hr"
                    full-width
                    v-model="isoConfirmationTimeFormatted"
                    @change="showTimePicker = false"
                  />
                </v-menu>
              </v-col>
            </v-row>
          </div>

          <v-textarea
            v-model="invoice.confirmation.text"
            auto-grow
            type="text"
            prepend-icon="mdi-account"
            rows="2"
            :label="confirmationTextPlaceholderShort"
            :rules="formRules.text"
          />
        </v-form>
      </div>
    </div>
    <template slot="actions">
      <v-spacer/>
      <v-btn
        color="primary"
        text
        :disabled="submitProcess"
        :loading="submitProcess"
        @click="submit"
      >
        Подтвердить
      </v-btn>
      <v-btn
        color="red"
        text
        :disabled="submitProcess"
        @click="toOrders"
      >
        Отмена
      </v-btn>
    </template>
  </InterfaceCard>
</template>

<script>
import Info from '@/components/Info'
import InterfaceCard from '@/components/InterfaceCard'
import PaymentConfirmationOptionSelector from '@/components/PaymentConfirmationOptionSelector'
import {ccy, types} from '@/constants'
import {
  getPaymentInfoFullTitle, invoiceInit, invoiceGetAmountTitle, invoiceGetToBalanceTitle,
  padWithZero, toBalanceCalc, toBalanceToggle
} from '@/services/utils'
import {InvoiceContext, InvoiceModel} from '@/services/invoices'
import {enableRefresh} from '@/services/vkApi'
import {getIsVkAppMobileAndroid} from '@/composables/context'

let _sourceId
let _buyoutId
let _invoiceId

export default {
  name: 'PaymentConfirmation',
  components: {PaymentConfirmationOptionSelector, Info, InterfaceCard},
  computed: {
    confirmationAmount () {
      return this.toBalance && this.toBalance.active ? this.toBalance.amountReal : this.invoice.confirmation.amount
    },
    confirmationImage () {
      return this.invoice.confirmation.imageBase64 || this.invoice.confirmation.image
    },
    receipt () {
      return this.confirmationImage || 'assets/images/no_photo.png'
    },
    confirmationIsValid () {
      return this.formIsValid && this.confirmationImageIsValid && this.paymentInfoIsValid
    },
    confirmationImageIsValid () {
      return this.confirmationTypeIsForm ? true : (this.confirmationAmount ? !!this.confirmationImage : true)
    },
    loadReceiptBtnTitle () {
      return getIsVkAppMobileAndroid() ? 'Прикрепите фото чека' : 'Прикрепите чек (фото или pdf)'
    },
    ruConfirmationDateFormatted: function () {
      return this.$root.$options.filters['localizedDateRu'](this.invoice.confirmation.date)
    },
    isoConfirmationDateFormatted: {
      get: function () {
        return this.invoice.confirmation.date.toISOString().substring(0, 10)
      },
      set: function (newDate) {
        const d = new Date(newDate)
        this.invoice.confirmation.date.setFullYear(d.getFullYear())
        this.invoice.confirmation.date.setMonth(d.getMonth())
        this.invoice.confirmation.date.setDate(d.getDate())
        this.invoice = Object.assign({}, this.invoice)
      }
    },
    isoConfirmationTimeFormatted: {
      get: function () {
        return `${padWithZero(this.invoice.confirmation.date.getHours())}:${padWithZero(this.invoice.confirmation.date.getMinutes())}`
      },
      set: function (newTime) {
        let [hours, minutes] = newTime.split(':')
        this.invoice.confirmation.date.setHours(hours)
        this.invoice.confirmation.date.setMinutes(minutes)
        this.invoice = Object.assign({}, this.invoice)
      }
    },
    labelAmount () {
      return invoiceGetAmountTitle(
        this.ccy,
        this.toBalance && this.toBalance.active && this.toBalance.value,
        this.invoice
      )
    },
    labelToBalanceActivator () {
      return invoiceGetToBalanceTitle(this.balanceCcy, this.toBalance.value)
    },
    labelUploadButton () {
      return this.confirmationImageIsValid ? 'Изменить чек' : 'Прикрепить чек'
    },
    paymentInfoIsValid () {
      return !this.paymentInfos.length || !!this.invoice.confirmation.paymentInfoId || this.isNullFullPrepayment
    },
    today () {
      return new Date().toISOString().slice(0, 10)
    },
    invoiceContext: function () {
      return new InvoiceContext(this.buyout, null, this.invoice)
    },
    invoiceModel: function () {
      return new InvoiceModel(this.ccy, this.invoice, this.invoiceContext)
    },
    isNullFullPrepayment: function () {
      return this.invoice.isPrepayed && this.invoice.confirmation.amount === 0 || this.invoice.confirmation.amount < 0
    }
  },
  created () {
    enableRefresh(false)
    _sourceId = this.$route.params.sourceId
    _buyoutId = this.$route.params.buyoutId
    _invoiceId = this.$route.params.invoiceId

    Promise.all([
      this.$client.get(this.$API.SOURCES.SETTINGS.CONFIRMATION_TYPE, {params: {sourceId: _sourceId}}),
      this.$client.get(this.$API.BALANCES.INFO, {params: {buyoutId: _buyoutId}}),
      this.$client.get(this.$API.BUYOUTS.ITEM, {params: {buyoutId: _buyoutId, describePaymentInfo: true}}),
      this.$client.get(this.$API.BUYOUTS.BUYOUT_ITEM_INVOICE, {params: {id: _invoiceId}})
    ])
    .then(([confirmationRs, balanceInfoRs, buyoutRs, invoiceRs]) => {
      this.confirmationTypeIsForm = confirmationRs.confirmationType === types.confirmation.form.id
      this.buyout = buyoutRs
      this.paymentInfos = []
      this.buyout.paymentInfos.forEach(pi => {
        pi.titleForShow = getPaymentInfoFullTitle(pi)
        if (this.buyout.paymentInfosMode === 'self' || pi.active) this.paymentInfos.push(pi)
      })
      this.balance = balanceInfoRs.balance
      this.ccy = ccy[this.buyout.buyoutCurrency].sign
      this._initInvoiceFormRs(invoiceRs)
    })
    .catch(err => this.error = err)
    .finally(() => this.loading = false)
  },
  beforeDestroy() {
    enableRefresh()
  },
  data () {
    return {
      balance: null,
      balanceCcy: ccy.RUB.sign,
      buyout: null,
      applicationFileName: false,
      confirmationTypeIsForm: null,
      confirmationTextPlaceholder: null,
      confirmationTextPlaceholderShort: null,
      ccy: null,
      error: null,
      formIsValid: false,
      formRules: {
        amount: [
          v => {
            if (!this.invoice.prepayment) {
              return !!v || 'Введите сумму оплаты'
            } else if (!this.invoice.isPrepayed && (this.invoice.prepayment.to < this.invoice.amount)) {
              return v >= this.invoice.prepayment.from && v <= this.invoice.prepayment.to || 'Введите сумму предоплаты'
            } else if (!this.invoice.isPrepayed && (this.invoice.prepayment.to >= this.invoice.amount)) {
              return v >= this.invoice.prepayment.from || 'Введите сумму предоплаты'
            } else if (this.invoice.isPrepayed) {
              (v + this.invoice.confirmationPrepayment.amount) === this.invoice.amount || 'Введите сумму доплаты'
            }
          },
          v => v > 0 || 'Сумма не должна быть отрицательной',
          v => {
            const parts = v.toString().split('.')
            return parts.length < 2 || parts.length > 1 && parts[1].length < 3 || 'не более 2х десятичных знаков'
          }
        ],
        amountToBalance: [
          v => v !== undefined && v !== null  || 'Введите сумму оплаты',
          v => v >= 0 || 'Сумма не должна быть отрицательной'
        ],
        date: [
          v => !!v || 'Введите дату оплаты'
        ],
        dateTime: [
          v => !!v || 'Введите часы оплаты'
        ],
        text: [
          v => this.confirmationTypeIsForm ? !!v || this.confirmationTextPlaceholder : true,
          v => !v || v.length <= 600 || 'Не более 600 символов'
        ]
      },
      invoice: null,
      loading: true,
      paymentInfos: null,
      showDatePicker: false,
      showTimePicker: false,
      submitProcess: false,
      toBalance: null
    }
  },
  methods: {
    changeToBalanceActive () {
      return toBalanceToggle(this.balance, this.invoice, this.toBalance)
    },
    paymentInfoChanged (newId) {
      if (newId) {
        this.invoice.confirmation.paymentInfoId = newId
        this.invoice = Object.assign({}, this.invoice)
      }
      let paymentInfo = this.paymentInfos.filter(val => val.id === this.invoice.confirmation.paymentInfoId)[0] || {}

      switch (paymentInfo.type) {
        case types.paymentInfo.phone.id:
          this.confirmationTextPlaceholder = 'ФИО владельца карты (полностью), номер телефона и ваш комментарий'
          this.confirmationTextPlaceholderShort = 'ФИО владельца карты, номер телефона и комментарий'
          break
        default:
          this.confirmationTextPlaceholder = 'ФИО владельца карты (полностью) и ваш комментарий'
          this.confirmationTextPlaceholderShort = 'ФИО владельца карты и комментарий'
          break
      }
    },
    async fileLoad () {
      const file = this.$refs.fileInput.files[0]
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        this.applicationFileName = file.type.startsWith('application') ? file.name : null
        this.invoice.confirmation.imageBase64 = reader.result
        this.invoice = Object.assign({}, this.invoice)
      };
    },
    fileSelect () {
      this.$refs.fileInput.click()
    },
    submit () {
      if (!this.formIsValid) {
        this.$refs.confirmationForm.validate()
      }
      if (!this.confirmationIsValid) {
        return
      }
      this.error = null
      const opt = {
        paymentInfoId: this.invoice.confirmation.paymentInfoId,
        amount: this.invoice.confirmation.amount,
        date: this.invoice.confirmation.date.getTime(),
        text: this.invoice.confirmation.text,
        imageBase64: this.invoice.confirmation.imageBase64
      }

      if (this.toBalance && this.toBalance.active){
        opt.amountReal = this.toBalance.amountReal
        opt.toBalance = this.toBalance.value
      }

      let paymentData = {
        invoiceId: _invoiceId,
        tsCustomer: new Date().getTime()
      }

      if (!this.invoice.prepayment
        || (this.invoice.prepayment && this.invoice.isPrepayed)
        || (opt.amount >= this.invoice.amount)) {
        paymentData.confirmation = opt
      } else {
        paymentData.confirmationPrepayment = opt
      }

      this.$client.post(this.$API.BUYOUTS.BUYOUT_ITEM_INVOICE_PAY, paymentData)
        .then(() => this.toOrders())
        .catch(err => this.error = err)
        .finally(() => this.submitProcess = false)

      this.submitProcess = true
    },
    toOrders(){
      this.$router.go(-1)
    },
    _initInvoiceFormRs (rs) {
      this.invoice = invoiceInit(rs)

      this.toBalance = toBalanceCalc(this.invoice, this.balance)
      this.paymentInfoChanged()
    }
  }
}
</script>

<style scoped lang="scss">
#file-input {
  display: none;
}
.confirmation-content {
  max-width: 640px;
  margin: 0 auto;
}
.confirmation-image {
  .file-select-btn {
    width: 100%;
  }
  .image-wrapper {
    margin: 0 auto;
    width: 250px;

    img {
      cursor: pointer;
      margin-bottom: 0.5rem;
      max-height: 100%;
      max-width: 100%;
    }
  }
  .receipt-error {
    height: 20px;
    margin-bottom: 1rem;
  }
}
</style>
